import { useState, useEffect, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { Capacitor } from '@capacitor/core';

type MessageCallback = (message: any) => void;

// Configuration constants
const CONFIG = {
  MAX_RECONNECT_ATTEMPTS: 5,
  RECONNECT_BASE_DELAY: 2000,
  JITTER: 1000,
  MAX_RECONNECT_DELAY: 30000,
  CONNECTION_TIMEOUT: 10000,
  AUTH_RETRY_DELAY: 1000,
  KEEP_ALIVE_INTERVAL: 30000
};

// Extracted event handlers to reduce nesting
const createKeepAliveInterval = (ws: WebSocket): NodeJS.Timeout => {
  return setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'PING' }));
      console.log('Sending ping to keep connection alive');
    }
  }, CONFIG.KEEP_ALIVE_INTERVAL);
};

export function useMessagingSocket() {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const { token, user } = useAuth();
  const { toast } = useToast();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const messageCallbacksRef = useRef<Map<string, MessageCallback>>(new Map());
  const authRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to send authentication message
  const sendAuthMessage = () => {
    if (!token || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return false;
    
    try {
      wsRef.current.send(JSON.stringify({
        type: 'MESSAGING_AUTH',
        token
      }));
      return true;
    } catch (error) {
      console.error('Error sending auth message:', error);
      return false;
    }
  };

  // Register a callback function for specific message types
  const registerMessageCallback = (type: string, callback: MessageCallback) => {
    messageCallbacksRef.current.set(type, callback);
    return () => {
      messageCallbacksRef.current.delete(type);
    };
  };

  // Send a message through the WebSocket
  const sendMessage = (messageData: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return false;
    }

    try {
      wsRef.current.send(JSON.stringify(messageData));
      return true;
    } catch (error) {
      console.error('Error sending message:', error);
      return false;
    }
  };

  useEffect(() => {
    let ws: WebSocket | null = null;
    let connectionTimeout: NodeJS.Timeout | null = null;
    let keepAliveInterval: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (connectionTimeout) clearTimeout(connectionTimeout);
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (keepAliveInterval) clearInterval(keepAliveInterval);
      if (authRetryTimeoutRef.current) clearTimeout(authRetryTimeoutRef.current);
      
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect attempts
        wsRef.current.onerror = null;
        wsRef.current.close();
      }
    };

    const connect = () => {
      if (!user || !token) return;
      
      cleanup(); // Clean up any existing connection first
      
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected');
        return;
      }

      setIsConnecting(true);
      
      // Create WebSocket URL based on environment
      let wsUrl;
      if (Capacitor.isNativePlatform()) {
        // For native apps, use the server URL from environment variables
        const serverUrl = import.meta.env.VITE_SERVER_URL ?? window.location.origin;
        const parsedUrl = new URL(serverUrl);
        wsUrl = `${parsedUrl.protocol === 'https:' ? 'wss:' : 'ws:'}//${parsedUrl.host}/ws`;
        console.log('Mobile WebSocket - using absolute URL:', wsUrl);
      } else {
        // For web, use relative path which automatically adapts to the current host
        wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
        console.log('Web WebSocket - using relative URL:', wsUrl);
      }
      
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
      } catch (error) {
        console.error('Error creating WebSocket:', error);
        setIsConnecting(false);
        handleReconnect();
        return;
      }
      
      // Set connection timeout
      connectionTimeout = setTimeout(() => {
        if (ws?.readyState !== WebSocket.OPEN) {
          console.log('Connection timeout, attempting reconnect');
          cleanup();
          handleReconnect();
        }
      }, CONFIG.CONNECTION_TIMEOUT);

      ws.onopen = () => {
        console.log('WebSocket connected');
        clearTimeout(connectionTimeout as NodeJS.Timeout);
        setIsConnecting(false);
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Only proceed if ws is still valid
        if (ws && token) {
          // Immediate authentication attempt
          if (!sendAuthMessage()) {
            // If initial auth fails, retry once after a short delay
            authRetryTimeoutRef.current = setTimeout(() => {
              if (ws.readyState === WebSocket.OPEN && !sendAuthMessage()) {
                console.error('Failed to authenticate after retry');
                cleanup();
                handleReconnect();
              }
            }, CONFIG.AUTH_RETRY_DELAY);
          }
          
          // Setup keep-alive ping interval
          if (keepAliveInterval) clearInterval(keepAliveInterval);
          keepAliveInterval = createKeepAliveInterval(ws);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket closed');
        cleanup();
        setIsConnected(false);
        handleReconnect();
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        cleanup();
        setIsConnected(false);
        handleReconnect();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received message:', data.type);

          // Handle message based on type
          if (data.type === 'MESSAGING_AUTH_SUCCESS') {
            console.log('Messaging authentication successful');
          } else if (data.type === 'ERROR') {
            console.error('WebSocket error:', data.message);
            if (data.message === 'No token provided' || data.message === 'Invalid token') {
              // Retry authentication once
              if (authRetryTimeoutRef.current === null) {
                authRetryTimeoutRef.current = setTimeout(() => {
                  if (ws.readyState === WebSocket.OPEN) {
                    sendAuthMessage();
                  }
                }, CONFIG.AUTH_RETRY_DELAY);
              } else {
                // If we've already retried, reconnect
                cleanup();
                handleReconnect();
              }
            } else {
              toast({
                title: 'Connection Error',
                description: data.message,
                variant: 'destructive'
              });
            }
          } else if (data.type === 'PONG') {
            // Handle pong response
            console.log('Received pong from server');
          } else {
            // For any unhandled message type, check if we have a registered callback
            const callback = messageCallbacksRef.current.get(data.type);
            if (callback) {
              callback(data);
            }
          }
        } catch (error) {
          console.error('Error parsing message:', error);
        }
      };
    };

    const handleReconnect = () => {
      if (reconnectAttemptsRef.current >= CONFIG.MAX_RECONNECT_ATTEMPTS) {
        toast({
          title: 'Connection Lost',
          description: 'Could not reconnect to messaging service. Please refresh the app.',
          variant: 'destructive'
        });
        return;
      }

      reconnectAttemptsRef.current++;
      console.log(`Attempting reconnect ${reconnectAttemptsRef.current}/${CONFIG.MAX_RECONNECT_ATTEMPTS}`);

      const delay = Math.min(
        CONFIG.RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttemptsRef.current) + 
        Math.random() * CONFIG.JITTER,
        CONFIG.MAX_RECONNECT_DELAY
      );

      reconnectTimeoutRef.current = setTimeout(connect, delay);
    };

    connect();

    // Cleanup on unmount
    return cleanup;
  }, [user, token, toast]);

  return {
    isConnected,
    isConnecting,
    registerMessageCallback,
    sendMessage
  };
}